import React, { useState, useEffect } from 'react';
import { Modal, Table, message, Spin } from 'antd';
import { UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { referralApi } from '../services/referralApi';
import { ReferredUser } from '../types';
import './ReferralListModal.css';

interface ReferralListModalProps {
  visible: boolean;
  onClose: () => void;
}

const ReferralListModal: React.FC<ReferralListModalProps> = ({ visible, onClose }) => {
  const [referrals, setReferrals] = useState<ReferredUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (visible) {
      fetchReferrals(1);
    }
  }, [visible]);

  const fetchReferrals = async (page: number) => {
    setLoading(true);
    try {
      const response = await referralApi.getList(page, pageSize);
      if (response.success && response.data) {
        setReferrals(response.data.referrals);
        setTotal(response.data.total);
        setCurrentPage(page);
      } else {
        message.error('Không thể tải danh sách người được giới thiệu');
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      message.error('Lỗi khi tải danh sách');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    fetchReferrals(page);
  };

  const maskPhone = (phone: string): string => {
    if (!phone) return '***';
    if (phone.length <= 4) return phone;
    const start = phone.substring(0, 3);
    const end = phone.substring(phone.length - 2);
    return `${start}***${end}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <div className="referral-name">
          <UserOutlined style={{ marginRight: 8 }} />
          {name}
        </div>
      ),
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => <span className="referral-phone">{maskPhone(phone)}</span>,
    },
    {
      title: 'Ngày tham gia',
      dataIndex: 'joinedAt',
      key: 'joinedAt',
      render: (date: string) => <span className="referral-date">{formatDate(date)}</span>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'rewardGiven',
      key: 'rewardGiven',
      render: (rewardGiven: boolean) => (
        <div className={`reward-status ${rewardGiven ? 'success' : 'pending'}`}>
          {rewardGiven ? (
            <>
              <CheckCircleOutlined /> Đã tặng voucher
            </>
          ) : (
            <>
              <CloseCircleOutlined /> Chưa tặng
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="referral-modal-header">
          <UserOutlined style={{ marginRight: 8 }} />
          Danh sách người được giới thiệu
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="referral-list-modal"
    >
      {loading ? (
        <div className="referral-loading">
          <Spin size="large" />
          <p>Đang tải danh sách...</p>
        </div>
      ) : (
        <>
          <div className="referral-summary">
            <span>Tổng số người được giới thiệu: </span>
            <strong className="total-count">{total}</strong>
          </div>
          <Table
            columns={columns}
            dataSource={referrals}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              showSizeChanger: false,
              showTotal: (total: number) => `Tổng ${total} người`,
            }}
            locale={{
              emptyText: 'Chưa có người được giới thiệu',
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default ReferralListModal;
